import {ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {AsyncPipe, CommonModule, NgForOf, NgIf} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {FullSearchService, IFullSearchState} from './full-search.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {debounceTime} from 'rxjs/operators';
import {Overlay, OverlayConfig, OverlayModule, OverlayRef} from '@angular/cdk/overlay';
import {PortalModule, CdkPortal} from '@angular/cdk/portal';
import {GenericListModule} from '../../_generalTools/generic-list/generic-list.module';
import {IGenericActions, IGenericListMultiAction, IGenericListSelection} from '../../_generalTools/generic-list/generic-list.component';
import {Column} from 'devextreme/ui/data_grid';
import {SurveysListActionsService} from '../surveys-list/surveys-list-actions.service';
import {DisplayUserDetail} from '@app/4dm1n/users-list/store/users-list.actions';
import {Store} from '@ngrx/store';
import {AppState} from '@store/app.states';
import {LoadingDirectiveModule} from '../../loading-directive/loading-directive.module';
import {AppUserContextService} from '../reference-lists/app-user-context.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ProjectsService} from '../reference-lists/projects.service';
import {NgStringPipesModule} from 'ngx-pipes';
import {SurveysActionsComponent} from "../surveys-list/surveys-actions/surveys-actions.component";
import {refTypesMatIcons} from "../reference-lists/references-status.service";
import {MatInputModule} from "@angular/material/input";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";

@Component({
  selector: 'app-full-search',
  templateUrl: './full-search.component.html',
  imports: [
    NgIf,
    LoadingDirectiveModule,
    AsyncPipe,
    TranslateModule,
    MatInputModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    MatIconModule,
    GenericListModule,
    MatButtonModule,
    NgForOf,
    SurveysActionsComponent,
    NgStringPipesModule,
    MatTooltipModule,
    PortalModule
  ],
  standalone: true,
  styleUrls: ['./full-search.component.scss']
})
export class FullSearchComponent extends OnDestroyMixin implements OnInit {

  constructor(
    protected overlay: Overlay,
    public fullSearchService: FullSearchService,
    private cdr: ChangeDetectorRef,
    private surveysListActionsService: SurveysListActionsService,
    private store: Store<AppState>,
    public appUserContextService: AppUserContextService,
    private translate: TranslateService,
    private projectsService: ProjectsService,
  ) {
    super();
  }

  @ViewChild('searchValue', {static: false}) searchValue: ElementRef<HTMLInputElement>;
  @ViewChild(CdkPortal)
  public contentTemplate: CdkPortal;

  protected overlayRef: OverlayRef;

  _hasOverlay = false;

  fSearch = new UntypedFormGroup({
    value: new UntypedFormControl(null, [
      Validators.minLength(3),
    ]),
  });

  filterValue: any = null;
  selectedfilterEntity: string = null;
  baseColumns: Column[] = [
    {
      caption: 'weight',
      dataField: 'weight',
      dataType: 'string',
      visible: false,
      sortOrder: 'asc',
      sortIndex: 1,
    },
    {
      caption: 'actions',
      dataType: 'number',
      dataField: 'actions',
      alignment: 'left',
      cellTemplate: 'firstExternalTemplate',
      width: 180,
    },
    {
      caption: 'entity',
      dataField: 'entity',
      dataType: 'string',
      visible: false,
    },
    {
      caption: 'id',
      dataField: 'id',
      dataType: 'string',
      cellTemplate: 'secondExternalTemplate',
      width: 75,
    },
    {
      caption: 'title',
      dataField: 'caption',
      dataType: 'string',
      width: '100%'
    },
    {
      caption: 'updated',
      dataField: 'updates',
      dataType: 'datetime',
      visible: false,
    },
    {
      caption: 'project',
      dataField: 'projectTitle',
      dataType: 'string',
      cellTemplate: 'thirdExternalTemplate',
    },
    {
      caption: 'organization',
      dataField: 'organizationName',
      dataType: 'string',
    },

  ];

  searchHistoryColumns: Column[] = [
    {
      dataField: 'keyword',
      caption: this.translate.instant('Mot clé'),
      dataType: 'string',
    },
    {
      dataField: 'occurrences',
      caption: this.translate.instant('Nb'),
      dataType: 'number'
    },
  ];
  historyListActions: IGenericActions = {
    general: [
      {
        name: 'clearHistory',
        matIcon: 'backspace',
        toolTip: this.translate.instant('Effacer l\'historique'),
      },
    ],
    rows: [],
    multiple: [],
  };

  @HostListener('window:resize')
  public onWinResize() {
    this.syncWidth();
  }

  ngOnInit(): void {

    this.fullSearchService.fullSearchState$
      .pipe(untilComponentDestroyed(this))
      .subscribe((state) => {

        // 1. Afficher la zone de texte...
        if (state.ux.barIsDisplayed) {
          // Afficher le textBox de recherche et lui donne le focus
          setTimeout(() => {
            if (this?.searchValue) {
              this.searchValue.nativeElement.focus();
            }
            this.showSearchResult();
          });
        } else {
          this.hideSearchResult();
        }

        // 2. Afficher le résultat de la recherche
        if (state.ux.searching) {
          this.showSearchResult();
        }
        // 3. On a un résultat : on l'affiche !
        if (state.ux.resultIsDisplayed) {
          this.showSearchResult();
        }

        if (!state.ux.resultIsDisplayed) {
          this.hideSearchResult();
        }
        // On aurait changé le texte à chercher ?
        const {value} = this.fSearch.getRawValue();
        if (state.ux.valueToSearch !== value) {
          this.fSearch.patchValue({value: state.ux.valueToSearch}, {emitEvent: false});
        }
        this.cdr.detectChanges();
      });

    //
    // Formulaire de recherche.
    //
    this.fSearch.valueChanges
      .pipe(
        debounceTime(100),
        untilComponentDestroyed(this),
      )
      .subscribe(() => {
        if (this.fSearch.valid) {
          setTimeout(() => {
            const {value} = this.fSearch.getRawValue();

            // <---------------- A ajouter.
            this.fullSearchService.fullSearchState$.next({
              ...this.fullSearchService.fullSearchState$.value,
              ux: {
                ...this.fullSearchService.fullSearchState$.value.ux,
                valueToSearch: value,
              }
            });

            if (value.length > 0) {
              console.log('searching for...', value);
              //
              // Partir le http...
              this.fullSearchService.doSearch(value);
              //
            } else {
              this.fullSearchService.toggleSearching(false);
            }
          }, 1000);

        }
      });

  }

  //
  // Affiche ou masque le champ de recherche....
  //
  toggleSearch() {
    setTimeout(() => {
      this.fullSearchService.toggleSearchBar();
    });
  }

  //
  // A ajouter sur le focusout
  //
  lostFocus() {
    if (!this._hasOverlay) {
      setTimeout(() => {
        this.fullSearchService.clearSearch();
      }, 200);
    }


  }

  // -----------------------------------------------------------------------------------------------------------------------------
  //
  // Search result display
  //
  showSearchResult() {

    if (!this._hasOverlay) {
      this.overlayRef = this.overlay.create(this.getOverlayConfig());
      this.overlayRef?.attach(this.contentTemplate);
      this.syncWidth();
      this.overlayRef.backdropClick()
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => {
          // console.log('stooooop');
          this.hideSearchResult();
          this.fullSearchService.clearSearch();
        });
      this._hasOverlay = true;
    }

  }

  public hideSearchResult() {
    if (this._hasOverlay) {
      this.overlayRef?.detach();
      this._hasOverlay = false;
    }
  }

  //
  // Poutine des overlays
  //
  protected getOverlayConfig(): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.searchValue)
      .withPush(false)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      }, {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      }]);
    const scrollStrategy = this.overlay.scrollStrategies.reposition();
    return new OverlayConfig({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });
  }

  private syncWidth() {
    if (!this.overlayRef) {
      return;
    }
    if (this.searchValue) {
      const refRect = this.searchValue.nativeElement.getBoundingClientRect();
      this.overlayRef.updateSize({width: refRect?.width - 12});
    }
    // console.log('SyncWidth!', refRect.width);
  }

  //
  // Actions de la liste de résultats !
  //
  doEntityAction(data) {

    this.fullSearchService.clearSearch();

    switch (data.entity) {
      case 'dashboards':
        this.surveysListActionsService.doAction({
          name: 'gotoDashboard',
          selection: {
            keys: [data.id],
            objects: [{...data, isV2: true}],
          }
        });
        break;

      case 'publications':
        this.surveysListActionsService.doAction({
          name: 'displaySlidePanelPublication',
          selection: {
            keys: [data.id],
            objects: [{...data, isV2: true}],
          }
        });
        break;

      case 'contributors':
        this.store.dispatch(DisplayUserDetail({userId: parseInt(data.id.split('_')[1], 0)}));
        break;

      case 'communities':
        this.surveysListActionsService.doAction({
          name: 'displayCommunity',
          selection: {
            keys: [data.id],
            objects: [{...data, isV2: true}],
          }
        });
        break;

      default:

    }

  }

  clearEntityFilter() {
    this.filterValue = null;
    this.selectedfilterEntity = null;
  }

  filterEntityClass(aEntity: string) {
    if (this.selectedfilterEntity === aEntity) {
      this.clearEntityFilter();
    } else {
      this.filterValue = ['entity', '=', aEntity];
      this.selectedfilterEntity = aEntity;
    }
  }

  historyListActionsCall(e: IGenericListMultiAction) {
    switch (e.name) {
      case 'clearHistory':
        this.appUserContextService.clearSearchHistory();
        break;
      default:
    }
  }

  historyChoose(e: IGenericListSelection) {
    if (e.keys.length > 0) {
      // console.log(e.keys[0]);
      this.fSearch.patchValue({value: e.keys[0]}, {emitEvent: true});
    }
  }

  displayHistory() {

    this.fullSearchService.displayHistory();

  }

  addProjectContext(ev: MouseEvent, organizationId: any, projectId: string) {

    ev.preventDefault();
    ev.stopPropagation();

    this.projectsService.addProjectContext(organizationId, projectId);

  }

  protected readonly refTypesMatIcons = refTypesMatIcons;
}
